<template>
  <WdsPopover v-model:open="open" placement="bottom-end" @update:open="onOpenChange">
    <template #trigger="{ togglePopover }">
      <WdsButton ref="triggerBtn" secondary :disabled="isGenerating" @click="togglePopover">
        <WdsStack align-center justify-center>
          <img
            v-if="isGenerating"
            src="/gifs/worksome-assistant-thinking.gif"
            style="height: 16px"
          />
          <WdsIcon v-else :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
          <div class="m-l-5">
            {{ isGenerating ? $t('Generating...') : $t('Create') }}
          </div>
        </WdsStack>
      </WdsButton>
    </template>

    <!-- TODO Set the width with a class -->
    <WdsStack direction-column class="new-experience p-15" style="width: 450px">
      <WdsInputArea
        id="description-input"
        v-model="briefDescription"
        placeholder="Provide a brief description of the job"
        :disabled="isGenerating"
        height="70px"
      />

      <WdsCheckbox v-if="hasDescription" v-model:checked="includeDescription" class="m-t-10">
        Include my current description
      </WdsCheckbox>

      <WdsStack justify-space-between class="m-t-10">
        <WdsText small disabled-text class="m-e-10">
          {{ $t('Worksome Intelligence AI prompt (do not enter personal or confidential data).') }}
        </WdsText>
        <WdsButton secondary :disabled="!briefDescription.trim() || isGenerating" @click="generate">
          {{ $t('Generate') }}
          <WdsIcon :name="WdsIconNameEnum.SEND_ALT" :size="20" />
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsPopover>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsInputArea,
  WdsStack,
  WdsText,
  WdsIcon,
  WdsPopover,
  WdsIconNameEnum,
  WdsCheckbox,
} from '@worksome/wds-components'
import axios from 'axios'
import { computed, ref } from 'vue'
import { useTranslation } from '@/composables/translation'

const { $t } = useTranslation()
const briefDescription = ref('')
const isGenerating = ref(false)
const emit = defineEmits(['generate-description'])
const open = ref(false)
const includeDescription = ref(true)

const props = defineProps({
  jobId: {
    type: String,
    required: false,
    default: null,
  },
  title: {
    type: String,
    required: true,
    default: null,
  },
  description: {
    type: String,
    required: true,
    default: null,
  },
})

const onOpenChange = async (isOpen) => {
  if (!isOpen) return

  // Focus the input area when the popover opens
  const inputArea = document.getElementById('description-input')
  if (inputArea) {
    inputArea.focus()
  }
}

const hasDescription = computed(() => props.description.length > 25)

const generate = async () => {
  if (!briefDescription.value.trim() || isGenerating.value) return

  open.value = false

  try {
    isGenerating.value = true

    const payload = {
      job_id: props.jobId,
      title: props.title,
      description: hasDescription.value && includeDescription.value ? props.description : null,
      user_message: briefDescription.value,
    }

    const response = await axios.post('/web-api/ai/generate-job-description', payload)

    if (response.data) {
      emit('generate-description', response.data.description)
    }
  } finally {
    isGenerating.value = false
  }
}
</script>

<style>
.new-experience {
  position: relative;
  padding: 8px;
  background: linear-gradient(
    54deg,
    #634cee 9.2%,
    rgba(99, 76, 238, 0.94) 26.8%,
    #f15c30 60.32%,
    #ffc800 93.01%
  );

  background-size: 100% 100%;
  animation: gradient-animation 5s ease-in-out 4;
  animation-fill-mode: forwards;
  border-radius: 0.25rem;
}
.new-experience::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: white;
  z-index: 1;
  border-radius: 0.25rem;
}
.new-experience > * {
  position: relative;
  z-index: 2;
}
</style>
